/**
 * 멤버 초대 모달 — OWNER/ADMIN 만 노출되는 흐름.
 * 이메일 + 역할 입력 → POST /organizations/:id/invitations.
 * 같은 이메일에 대한 pending 초대는 서버가 자동 invalidate + 새 토큰 발급.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createInvitation, type MemberRole } from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

interface Props {
  open: boolean;
  organizationId: string;
  organizationName: string;
  /** 초대자 본인의 역할 — OWNER/ADMIN. 부여 가능한 role 옵션 결정. */
  inviterRole: MemberRole;
  onClose: () => void;
  onInvited: () => void;
}

const ROLE_LABEL: Record<MemberRole, string> = {
  OWNER: "소유자",
  ADMIN: "관리자",
  MEMBER: "멤버",
  VIEWER: "뷰어",
};

const ROLE_RANK: Record<MemberRole, number> = {
  OWNER: 4,
  ADMIN: 3,
  MEMBER: 2,
  VIEWER: 1,
};

export function InviteMemberDialog({
  open,
  organizationId,
  organizationName,
  inviterRole,
  onClose,
  onInvited,
}: Props): JSX.Element | null {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("MEMBER");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setRole("MEMBER");
    setError(null);
    setSubmitting(false);
    setTimeout(() => emailRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // 초대자 본인 이하 역할만 부여 가능.
  const allowedRoles: MemberRole[] = (
    ["OWNER", "ADMIN", "MEMBER", "VIEWER"] as MemberRole[]
  ).filter((r) => ROLE_RANK[r] <= ROLE_RANK[inviterRole]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createInvitation(organizationId, { email, role });
      onInvited();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "초대 발송에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">멤버 초대</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              <strong>{organizationName}</strong> 에 새 멤버를 초대합니다. 입력한
              이메일로 7일 유효한 초대 링크가 발송됩니다.
            </p>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="invite-email" className="text-sm font-medium">
              이메일
            </label>
            <Input
              id="invite-email"
              ref={emailRef}
              type="email"
              required
              maxLength={254}
              className="mt-1.5"
              placeholder="newmember@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="invite-role" className="text-sm font-medium">
              역할
            </label>
            <select
              id="invite-role"
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={role}
              onChange={(e) => setRole(e.target.value as MemberRole)}
            >
              {allowedRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              본인보다 높은 역할은 부여할 수 없습니다.
            </p>
          </div>
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              초대 발송
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
